import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Box, Button, Typography, Paper, List, ListItem, ListItemText,
  Tooltip, Divider, Chip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { reorderEpics, getEffectiveStoryPoints, Epic } from '../../store/slices/epicsSlice';
import { updateWorkstream } from '../../store/slices/workstreamsSlice';
import type { StoryType } from '../../store/slices/storiesSlice';

const POD_ZONE_PREFIX = 'pod-zone-';
const UNASSIGNED_POD_ZONE = `${POD_ZONE_PREFIX}__unassigned__`;

const UNASSIGNED_ID = '__unassigned__';

const EpicPlanner: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const workstreams = useAppSelector((state) => state.workstreams.workstreams);
  const allEpics = useAppSelector((state) => state.epics.epics);
  const categories = useAppSelector((state) => state.categories.categories);
  const pods = useAppSelector((state) => state.pods.pods);
  const allWorkstreamSprints = useAppSelector((state) => state.sprints.workstreamSprints);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(
    () => (localStorage.getItem('epicPlannerViewMode') as 'list' | 'grid') || 'grid'
  );

  const getEpicsForWorkstream = (workstreamId: string | null) =>
    allEpics
      .filter((e) => e.workstreamId === workstreamId)
      .sort((a, b) => a.priority - b.priority);

  const getCategoryColor = (categoryId: string | null) => {
    if (!categoryId) return '#9e9e9e';
    return categories.find((c) => c.id === categoryId)?.color || '#9e9e9e';
  };

  const getStoriesForType = (epic: Epic, type: StoryType) =>
    (epic.stories ?? []).filter((s) => s.type === type);

  const isManualOverride = (epic: Epic, type: StoryType): boolean => {
    const typeStories = getStoriesForType(epic, type);
    return typeStories.length === 0 && epic.storyPointOverrides?.[type] !== undefined;
  };

  const getChipTooltip = (epic: Epic, type: StoryType): React.ReactNode => {
    const typeStories = getStoriesForType(epic, type);
    if (typeStories.length === 0) {
      if (epic.storyPointOverrides?.[type] !== undefined) {
        return 'No stories, override estimation';
      }
      return 'No stories';
    }
    const totalLabel = `${typeStories.length} ${typeStories.length === 1 ? 'Story' : 'Stories'}`;
    if (type === 'Engineering') {
      const subtypeGroups: Record<string, { count: number; pts: number }> = {};
      typeStories.forEach((s) => {
        const key = s.engineeringSubtype ?? 'General';
        if (!subtypeGroups[key]) subtypeGroups[key] = { count: 0, pts: 0 };
        subtypeGroups[key].count += 1;
        subtypeGroups[key].pts += (s.points ?? 0);
      });
      return (
        <Box>
          <Box sx={{ mb: 0.5 }}>{totalLabel}</Box>
          {Object.entries(subtypeGroups).map(([subtype, { count, pts }]) => (
            <Box key={subtype}>{count} {subtype} - {pts} pts</Box>
          ))}
        </Box>
      );
    }
    const totalPts = typeStories.reduce((sum, s) => sum + (s.points ?? 0), 0);
    return (
      <Box>
        <Box sx={{ mb: 0.5 }}>{totalLabel}</Box>
        <Box>{totalPts} pts</Box>
      </Box>
    );
  };

  // Pod helpers ----------------------------------------------------------------

  /** All pod IDs currently assigned to at least one workstream. */
  const assignedPodIds = new Set(
    workstreams.flatMap((ws) => ws.entries[0]?.podIds ?? [])
  );

  /** Pods not yet assigned to any workstream. */
  const unassignedPods = pods.filter((p) => !assignedPodIds.has(p.id));

  /** Returns the pods assigned to a given workstream (via its default entry). */
  const getPodsForWorkstream = (wsId: string) => {
    const ws = workstreams.find((w) => w.id === wsId);
    const podIds = ws?.entries[0]?.podIds ?? [];
    return pods.filter((p) => podIds.includes(p.id));
  };

  // ---------------------------------------------------------------------------

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination, type } = result;

    // ── Pod reassignment ────────────────────────────────────────────────────
    if (type === 'POD') {
      if (source.droppableId === destination.droppableId) return;

      const podId = result.draggableId.replace('pod-', '');
      const sourceZone = source.droppableId;
      const destZone = destination.droppableId;
      const now = new Date().toISOString();

      // Remove from source workstream (ignore if coming from the unassigned pool)
      if (sourceZone !== UNASSIGNED_POD_ZONE) {
        const sourceWsId = sourceZone.replace(POD_ZONE_PREFIX, '');
        const sourceWs = workstreams.find((w) => w.id === sourceWsId);
        if (sourceWs) {
          const newPodIds = (sourceWs.entries[0]?.podIds ?? []).filter((id) => id !== podId);
          dispatch(updateWorkstream({
            ...sourceWs,
            entries: newPodIds.length > 0 ? [{ sprintId: 'default', podIds: newPodIds }] : [],
            updatedAt: now,
          }));
        }
      }

      // Add to destination workstream (ignore if dropped back onto the unassigned pool)
      if (destZone !== UNASSIGNED_POD_ZONE) {
        const destWsId = destZone.replace(POD_ZONE_PREFIX, '');
        const destWs = workstreams.find((w) => w.id === destWsId);
        if (destWs) {
          const existingPodIds = destWs.entries[0]?.podIds ?? [];
          if (!existingPodIds.includes(podId)) {
            dispatch(updateWorkstream({
              ...destWs,
              entries: [{ sprintId: 'default', podIds: [...existingPodIds, podId] }],
              updatedAt: now,
            }));
          }
        }
      }

      return;
    }

    // ── Epic reordering / reassignment ───────────────────────────────────────
    const sourceId = source.droppableId;
    const destId = destination.droppableId;

    const toWorkstreamId = (id: string): string | null =>
      id === UNASSIGNED_ID ? null : id;

    const sourceWsId = toWorkstreamId(sourceId);
    const destWsId = toWorkstreamId(destId);

    if (sourceId === destId) {
      const wsEpics = getEpicsForWorkstream(sourceWsId);

      // Prevent dragging closed epics (belt-and-suspenders; isDragDisabled handles the UI)
      const draggedEpic = wsEpics.find((e) => e.id === result.draggableId);
      if (draggedEpic?.parentState === 'Closed') return;

      const items = Array.from(wsEpics);
      const [removed] = items.splice(source.index, 1);
      items.splice(destination.index, 0, removed);

      // Re-pin closed epics at their original positions so dragging non-closed
      // epics never changes the relative order of closed ones.
      const closedAtIdx: Array<{ idx: number; epic: (typeof items)[number] }> = [];
      wsEpics.forEach((e, i) => {
        if (e.parentState === 'Closed') closedAtIdx.push({ idx: i, epic: e });
      });
      if (closedAtIdx.length > 0) {
        const closedIdSet = new Set(closedAtIdx.map((x) => x.epic.id));
        const nonClosed = items.filter((e) => !closedIdSet.has(e.id));
        const rebuilt: Array<(typeof items)[number] | undefined> = new Array(items.length);
        closedAtIdx.forEach(({ idx, epic: ce }) => { rebuilt[idx] = ce; });
        let ni = 0;
        for (let i = 0; i < rebuilt.length; i++) {
          if (rebuilt[i] === undefined) rebuilt[i] = nonClosed[ni++];
        }
        items.length = 0;
        rebuilt.forEach((e) => { if (e !== undefined) items.push(e); });
      }

      const reordered = items.map((e, i) => ({ ...e, priority: i }));
      dispatch(reorderEpics({ workstreamId: sourceWsId, epics: reordered }));
    } else {
      const epicId = result.draggableId;
      const movedEpic = allEpics.find((e) => e.id === epicId);
      if (!movedEpic) return;

      const updatedSourceEpics = getEpicsForWorkstream(sourceWsId)
        .filter((e) => e.id !== epicId)
        .map((e, i) => ({ ...e, priority: i }));

      const destEpics = getEpicsForWorkstream(destWsId);
      const newDestEpics = [...destEpics];
      newDestEpics.splice(destination.index, 0, { ...movedEpic, workstreamId: destWsId });
      const updatedDestEpics = newDestEpics.map((e, i) => ({ ...e, priority: i }));

      dispatch(reorderEpics({ workstreamId: sourceWsId, epics: updatedSourceEpics }));
      dispatch(reorderEpics({ workstreamId: destWsId, epics: updatedDestEpics }));
    }
  };

  const unassignedEpics = getEpicsForWorkstream(null);

  // Containers: one per workstream + one for unassigned epics (if any exist or no workstreams)
  const showUnassigned = unassignedEpics.length > 0 || workstreams.length === 0;

  // Render the draggable pod zone for a given workstream (or the unassigned pod pool)
  const renderPodZone = (droppableId: string, zonePods: typeof pods) => (
    <Droppable droppableId={droppableId} type="POD" direction="horizontal">
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            minHeight: 36,
            p: 0.75,
            borderRadius: 1,
            border: '1.5px dashed',
            borderColor: snapshot.isDraggingOver ? 'primary.main' : 'divider',
            bgcolor: snapshot.isDraggingOver ? 'action.selected' : 'action.hover',
            transition: 'border-color 0.15s, background-color 0.15s',
            alignItems: 'center',
          }}
        >
          {zonePods.map((pod, index) => (
            <Draggable key={pod.id} draggableId={`pod-${pod.id}`} index={index}>
              {(dragProvided, dragSnapshot) => (
                <Box
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontSize: '0.65rem',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      opacity: dragSnapshot.isDragging ? 0.8 : 1,
                      boxShadow: dragSnapshot.isDragging ? 3 : 0,
                      transition: 'box-shadow 0.15s, opacity 0.15s',
                    }}
                  >
                    <span
                      {...dragProvided.dragHandleProps}
                      role="button"
                      aria-label="Drag to reassign pod"
                      tabIndex={0}
                      style={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                    </span>
                    <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit', lineHeight: 1.2 }}>
                      {pod.name}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
          {zonePods.length === 0 && !snapshot.isDraggingOver && (
            <Typography variant="caption" color="text.disabled" sx={{ alignSelf: 'center', px: 0.5 }}>
              Drop pods here
            </Typography>
          )}
        </Box>
      )}
    </Droppable>
  );

  const renderEpicList = (
    droppableId: string,
    epics: typeof allEpics,
    emptyLabel: string,
    createUrl: string,
  ) => (
    <Droppable droppableId={droppableId}>
      {(provided) => (
        <List
          dense
          ref={provided.innerRef}
          {...provided.droppableProps}
          sx={{ minHeight: 48 }}
        >
          {epics.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <AssignmentIcon color="disabled" />
              <Typography variant="body2" color="text.secondary">
                {emptyLabel}{' '}
                <Button size="small" onClick={() => navigate(createUrl)}>
                  Create Epic
                </Button>
              </Typography>
            </Box>
          )}
          {epics.map((epic, index) => {
            const isClosed = epic.parentState === 'Closed';
            return (
              <Draggable key={epic.id} draggableId={epic.id} index={index} isDragDisabled={isClosed}>
                {(dragProvided) => (
                  <ListItem
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    onClick={() => navigate(`/epic-planner/${epic.id}/edit`)}
                    sx={{
                      mb: 0.5,
                      borderRadius: 1,
                      borderLeft: `4px solid ${getCategoryColor(epic.categoryId)}`,
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      cursor: 'pointer',
                      opacity: isClosed ? 0.6 : 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Box
                      {...(isClosed ? {} : dragProvided.dragHandleProps)}
                      sx={{ mr: 1, cursor: isClosed ? 'not-allowed' : 'grab', color: isClosed ? 'text.disabled' : 'text.secondary' }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 300, fontWeight: 500, textDecoration: isClosed ? 'line-through' : 'none', color: isClosed ? 'text.disabled' : 'inherit' }}
                        >
                          {epic.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {isClosed && (
                            <Chip label="Closed" size="small" sx={{ height: 16, fontSize: '0.6rem', color: 'text.disabled', bgcolor: 'action.disabledBackground', '& .MuiChip-label': { px: 0.75 } }} />
                          )}
                          {(['Engineering'] as const).map((type) => {
                            const typeLabels: Record<StoryType, string> = { Engineering: 'Eng' };
                            const manualOverride = isManualOverride(epic, type);
                            return (
                              <Tooltip key={type} title={getChipTooltip(epic, type)}>
                                <Chip
                                  label={`${typeLabels[type]}: ${getEffectiveStoryPoints(epic, type)}`}
                                  size="small"
                                  color={manualOverride ? 'warning' : 'default'}
                                  variant={manualOverride ? 'outlined' : 'filled'}
                                  sx={{ fontSize: '0.65rem' }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>
                      }
                    />
                  </ListItem>
                )}
              </Draggable>
            );
          })}
          {provided.placeholder}
        </List>
      )}
    </Droppable>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => navigate('/epic-planner/new')}
          >
            New Epic
          </Button>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => navigate('/epic-planner/workstreams/new')}
          >
            New Workstream
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_e, val) => { if (val) { setViewMode(val); localStorage.setItem('epicPlannerViewMode', val); } }}
            aria-label="view mode"
          >
            <Tooltip title="List View">
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Grid View">
              <ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
      </Box>
      {workstreams.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No workstreams configured. Use the <strong>New Workstream</strong> button above to create one.
        </Typography>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Box
          sx={
            viewMode === 'grid'
              ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 3,
              }
              : { display: 'flex', flexDirection: 'column', gap: 3 }
          }
        >
          {workstreams.map((ws) => {
            const wsEpics = getEpicsForWorkstream(ws.id);
            const wsPods = getPodsForWorkstream(ws.id);
            const wsSprintCount = allWorkstreamSprints.filter((s) => s.workstreamId === ws.id).length;
            return (
              <Paper key={ws.id} elevation={2} sx={{ p: 2 }}>
                <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip
                      title={ws.description || ''}
                      arrow
                      placement="top"
                      disableHoverListener={!ws.description}
                      disableFocusListener={!ws.description}
                      disableTouchListener={!ws.description}
                    >
                      <Chip
                        label={ws.name}
                        variant="outlined"
                        clickable
                        onClick={() => navigate(`/epic-planner/workstreams/${ws.id}/edit`)}
                        aria-label={`Edit workstream ${ws.name}`}
                        sx={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          height: 'auto',
                          py: 0.75,
                          borderWidth: 2,
                          cursor: 'pointer',
                          '& .MuiChip-label': { px: 1.5 },
                          '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Badge badgeContent={wsSprintCount || null} color="primary">
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<EventNoteIcon />}
                      onClick={() => navigate(`/epic-planner/workstreams/${ws.id}/sprint-planner`)}
                      aria-label={`Open sprint planner for ${ws.name}`}
                    >
                      Sprint Planner
                    </Button>
                  </Badge>
                </Box>
                <Divider sx={{ mb: 1 }} />
                {/* Draggable pod zone */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
                  >
                    Pods
                  </Typography>
                  {renderPodZone(`${POD_ZONE_PREFIX}${ws.id}`, wsPods)}
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
                >
                  Epics
                </Typography>
                {renderEpicList(
                  ws.id,
                  wsEpics,
                  'No Epics Assigned.',
                  `/epic-planner/new?workstreamId=${ws.id}`,
                )}
              </Paper>
            );
          })}
          {showUnassigned && (
            <Paper key={UNASSIGNED_ID} elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom color="text.secondary">
                Unassigned
              </Typography>

              <Divider sx={{ mb: 1 }} />
              {renderEpicList(
                UNASSIGNED_ID,
                unassignedEpics,
                'No unassigned epics.',
                '/epic-planner/new',
              )}
            </Paper>
          )}
          {unassignedPods.length > 0 && (
            <Paper elevation={2} sx={{ p: 2, borderStyle: 'dashed', borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight={600} gutterBottom color="text.secondary">
                Pod Pool
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Unassigned pods — drag into a workstream to assign.
              </Typography>
              {renderPodZone(UNASSIGNED_POD_ZONE, unassignedPods)}
            </Paper>
          )}
        </Box>
      </DragDropContext>
    </Box>
  );
};

export default EpicPlanner;
